import { useMemo, useState } from 'react';
import {
  useGetSellerContactsPage,
  type SellerContactsPageFilters,
} from '~/api/sellerContacts';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import type { SellerContactsPageQuery } from '~/graphql/graphql';
import type { ContactDetails, ContactsStatus } from '~/utils/contact';

const PAGE_SIZE = 20;
const PINNED_PAGE_SIZE = 100;

const selectSellerContacts = (data: SellerContactsPageQuery | undefined) =>
  data?.sellerContactsPage;

function withoutListed(
  contacts: ContactDetails[],
  listedIds: ReadonlySet<string>
) {
  return contacts.filter(({ id }) => !listedIds.has(id));
}

/**
 * Pages through the contacts of a seller, with the `pinnedContactIds` contacts fetched in full
 * and listed first. The pinned ids are read on mount only, so the list does not move while the
 * buyer changes the selection. Contacts passed to `listAdded` follow the pinned ones. Key the
 * caller by organization: the hook keeps its state when `organizationId` changes.
 *
 * @example
 *   const { contacts, status, sentinelRef } = useSellerContactsPage(organizationId, [
 *     ...initialContactIds,
 *   ]);
 */
export function useSellerContactsPage(
  organizationId: string,
  pinnedContactIds: string[]
) {
  const [pinnedIds] = useState(() =>
    [...new Set(pinnedContactIds)].sort((a, b) => a.localeCompare(b))
  );
  const [addedContacts, setAddedContacts] = useState<ContactDetails[]>([]);
  const hasPinned = pinnedIds.length > 0;

  const pinnedFilters = useMemo<SellerContactsPageFilters>(
    () => ({ organizationId, onlyContactIds: pinnedIds }),
    [organizationId, pinnedIds]
  );
  const pagedFilters = useMemo<SellerContactsPageFilters>(
    () => ({
      organizationId,
      excludeContactIds: hasPinned ? pinnedIds : undefined,
    }),
    [organizationId, pinnedIds, hasPinned]
  );

  const pinned = usePaginatedQuery({
    fetchHook: useGetSellerContactsPage,
    select: selectSellerContacts,
    options: {
      pageSize: PINNED_PAGE_SIZE,
      filters: pinnedFilters,
      eager: true,
      enabled: hasPinned,
    },
  });
  const paged = usePaginatedQuery({
    fetchHook: useGetSellerContactsPage,
    select: selectSellerContacts,
    options: { pageSize: PAGE_SIZE, filters: pagedFilters },
  });

  const pinnedError = hasPinned ? pinned.error : null;
  const pinnedListed =
    pinned.data.length > 0 || pinned.pagination !== undefined;
  const pinnedLoaded =
    !hasPinned ||
    pinned.hasMoreData === false ||
    (pinnedError !== null && pinnedListed);
  const pagedLoaded =
    paged.data.length > 0 ||
    (paged.pagination !== undefined && paged.pagination.totalElements === 0);
  const loadError = pinnedError ?? paged.error;

  const pinnedListedIds = new Set(pinned.data.map(({ id }) => id));
  const added = withoutListed(addedContacts, pinnedListedIds);
  const listedIds = new Set([...pinnedListedIds, ...added.map(({ id }) => id)]);
  const contacts = [
    ...pinned.data,
    ...added,
    ...withoutListed(paged.data, listedIds),
  ];

  const firstLoaded = pinnedLoaded && pagedLoaded;
  const [firstLoadDone, setFirstLoadDone] = useState(firstLoaded);
  if (firstLoaded && !firstLoadDone) {
    setFirstLoadDone(true);
  }

  let status: ContactsStatus = 'loaded';
  if (!firstLoadDone) {
    status = loadError === null ? 'loading' : 'failed';
  }

  const retry = () => {
    if (pinnedError !== null) {
      pinned.refetch();
    }
    if (paged.error !== null) {
      paged.refetch();
    }
  };

  const listAdded = (contact: ContactDetails) =>
    setAddedContacts((previous) => [...previous, contact]);

  return {
    contacts,
    status,
    listAdded,
    sentinelRef: paged.sentinelRef,
    isLoadingMore: paged.isLoadingMore,
    hasMore: paged.hasMoreData !== false,
    loadFailed: status === 'loaded' && loadError !== null,
    retry,
  };
}
